import debounce from 'lodash/debounce'
import type {
  SelectionId,
  SelectionItem,
  SelectionRange,
  SelectionType,
} from 'types/selection'
import mixpanel from '@disco/api/mixpanel'
import {
  ADD_EMPTY_SELECTION,
  REMOVE_DATA_TABLE_SELECTION,
  REMOVE_SELECTION,
  SELECTION_CHANGE,
  SELECTION_RESET,
  UPDATE_SELECTION_ITEMS,
} from '@disco/constants/ActionTypes'
const trackExplorerSelection = debounce(() => {
  mixpanel.sendPureEvent('Multiple select explorer')
}, 10000)
const trackSearchShortlists = debounce(() => {
  mixpanel.sendPureEvent('Multiple select shortlist')
}, 10000)

const trackDataTableSelection = debounce((location: string) => {
  mixpanel.sendPureEvent(`Multiple select data table: ${location}`)
}, 10000)
/**
 * Adds a new empty selection container to the store.
 * @param params - Selection parameters object
 * @param params.selectionId - Unique identifier for the selection
 * @param params.selectionItems - Array of items in the selection
 * @param params.paneIndex - Index of the pane containing the selection
 * @returns Action object to add empty selection
 */
export function addEmptySelection({
  selectionId,
  selectionItems,
  paneIndex,
}: {
  selectionId: SelectionId
  selectionItems: SelectionItem[]
  paneIndex: number | null | undefined
}): {
  paneIndex: number | null | undefined
  selectionId: SelectionId
  selectionItems: Array<SelectionItem>
  type: string
} {
  return {
    type: ADD_EMPTY_SELECTION,
    selectionId,
    selectionItems,
    paneIndex,
  }
}
/**
 * Removes a selection from the store by its ID.
 * @param selectionId - The ID of the selection to remove
 * @returns Action object to remove the selection
 */
export function removeSelection(selectionId: SelectionId): {
  selectionId: SelectionId
  type: string
} {
  return {
    type: REMOVE_SELECTION,
    selectionId,
  }
}
/**
 * Updates the items in an existing selection.
 * @param params - Selection update parameters object
 * @param params.selectionId - ID of the selection to update
 * @param params.selectionItems - New array of selected items
 * @param params.paneIndex - Index of the pane containing the selection
 * @returns Action object to update selection items
 */
export function updateSelectionItems({
  selectionId,
  selectionItems,
  paneIndex,
}: {
  selectionId: SelectionId
  selectionItems: SelectionItem[]
  paneIndex: number | null | undefined
}): {
  paneIndex: number | null | undefined
  selectionId: SelectionId
  selectionItems: Array<SelectionItem>
  type: string
} {
  return {
    type: UPDATE_SELECTION_ITEMS,
    selectionId,
    selectionItems,
    paneIndex,
  }
}

function selectItems(
  selectionId: SelectionId,
  selectionRange: SelectionRange,
  selectionType: SelectionType,
) {
  return {
    type: SELECTION_CHANGE,
    selectionId,
    selectionRange: selectionRange,
    selectionType: selectionType,
  }
}

/**
 * Selects items in the explorer view and tracks analytics.
 * @param selectionId - ID of the selection
 * @param selectionRange - Range of items being selected
 * @param selectionType - Type of selection (add, remove, toggle, etc.)
 * @returns Action object to select explorer items
 */
export function selectExplorerItems(
  selectionId: SelectionId,
  selectionRange: SelectionRange,
  selectionType: SelectionType,
): {
  selectionId: SelectionId
  selectionRange: SelectionRange
  selectionType: SelectionType
  type: string
} {
  trackExplorerSelection()
  return selectItems(selectionId, selectionRange, selectionType)
}
/**
 * Selects items in shortlist view and tracks analytics.
 * @param selectionId - ID of the selection
 * @param selectionRange - Range of items being selected
 * @param selectionType - Type of selection (add, remove, toggle, etc.)
 * @returns Action object to select shortlist items
 */
export function selectShortlistItems(
  selectionId: SelectionId,
  selectionRange: SelectionRange,
  selectionType: SelectionType,
): {
  selectionId: SelectionId
  selectionRange: SelectionRange
  selectionType: SelectionType
  type: string
} {
  trackSearchShortlists()
  return selectItems(selectionId, selectionRange, selectionType)
}

export function selectDataTableItems(
  selectionId: SelectionId,
  selectionRange: SelectionRange,
  selectionType: SelectionType,
  location: string,
): {
  selectionId: SelectionId
  selectionRange: SelectionRange
  selectionType: SelectionType
  type: string
} {
  trackDataTableSelection(location)
  return selectItems(selectionId, selectionRange, selectionType)
}
/**
 * Resets all selections to their initial state.
 * @returns Action object to reset all selections
 */
export const resetSelection = (): {
  type: string
} => ({
  type: SELECTION_RESET,
})

export const clearDataTableSelection = (
  selectionId: SelectionId,
): { type: typeof REMOVE_DATA_TABLE_SELECTION; selectionId: SelectionId } => ({
  type: REMOVE_DATA_TABLE_SELECTION,
  selectionId,
})
